// ============================================================================
// Meal Forecast and Unused Package Meals (buffet check-off slice 4)
// ============================================================================
// Mirrors crs-api MealForecastDtos.cs (GET /MealForecast, GET /MealForecast/unused).
// The same forecast feeds the mobile Meals tab.

export interface MealForecastPlanShare {
  planCode: string;
  planName: string;
  pax: number;
  /** A MAP plan: lunch or dinner by the guest's choice. */
  isGuestChoice: boolean;
}

export interface MealForecastMeal {
  /** Checked-in guests expected at the meal (MAP by the day's choice, else the stay preference, else the plan). */
  inHouseAdults: number;
  inHouseChildren: number;
  /** Confirmed bookings not yet checked in. */
  arrivingAdults: number;
  arrivingChildren: number;
  /** Tentative, blocked and pending-approval bookings. */
  tentativeAdults: number;
  tentativeChildren: number;
  /** MAP guests whose day says "either" — on both meals, in neither total. */
  mayComeAdults: number;
  mayComeChildren: number;
  /** Meal add-ons mapped to this meal. */
  extraPax: number;
  /** Plan guests marked served by the restaurant (business date and earlier). */
  servedAdults: number;
  servedChildren: number;
  total: number;
  composition: MealForecastPlanShare[];
}

export interface MealForecastExtraItem {
  itemName: string;
  mealType: string | null;
  countedInTotals: boolean;
  pax: number;
  bookingNumber: string;
  groupName: string | null;
  remarks: string | null;
}

export interface MealForecastDay {
  date: string;
  dayOfWeek: string;
  breakfast: MealForecastMeal;
  lunch: MealForecastMeal;
  dinner: MealForecastMeal;
  inHouseOnEpPax: number;
  extraItems: MealForecastExtraItem[];
}

export interface MealForecast {
  propertyId: string;
  businessDate: string;
  fromDate: string;
  days: number;
  series: MealForecastDay[];
}

export type MealAllowance = 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'MAJOR';

export const ALLOWANCE_LABEL: Record<string, string> = {
  BREAKFAST: 'Breakfast',
  LUNCH: 'Lunch',
  DINNER: 'Dinner',
  MAJOR: 'MAP meal (lunch or dinner)',
};

export interface UnusedMealRow {
  date: string;
  bookingId: string;
  bookingNumber: string;
  guestName: string | null;
  rooms: string;
  planCode: string;
  allowance: MealAllowance;
  entitledAdults: number;
  entitledChildren: number;
  usedAdults: number;
  usedChildren: number;
  notComingAdults: number;
  notComingChildren: number;
  unusedAdults: number;
  unusedChildren: number;
}

export interface UnusedMealSummary {
  planCode: string;
  planName: string;
  allowance: MealAllowance;
  entitled: number;
  used: number;
  unused: number;
}

export interface UnusedMealReport {
  propertyId: string;
  fromDate: string;
  toDate: string;
  /** The day before the business date: the last date the report covers. */
  lastClosedDate: string;
  rows: UnusedMealRow[];
  summary: UnusedMealSummary[];
}
